<template>
  <div class="cycle-diagram">
    <div class="cycle-ring-track">
      <div class="cycle-ring-dot" />
    </div>
    <div class="cycle-center">
      <Transition
        name="step-fade"
        mode="out-in"
      >
        <div
          :key="activeStepIndex"
          class="cycle-center-body"
        >
          <strong class="cycle-center-keyword">{{ cycleSteps[activeStepIndex].keyword }}</strong>
          <span class="cycle-center-desc">{{ cycleSteps[activeStepIndex].desc }}</span>
        </div>
      </Transition>
    </div>
    <button
      v-for="(step, i) in cycleSteps"
      :key="i"
      class="cycle-node"
      :class="{ 'cycle-node--active': activeStepIndex === i }"
      :style="{ '--sa': `${-90 + i * 72}deg`, '--ca': `${90 - i * 72}deg` }"
      :aria-label="step.keyword + ': ' + step.desc"
      @click="activeStepIndex = i"
    >
      <div class="cycle-node-card">
        <strong class="cycle-node-label">{{ step.keyword }}</strong>
      </div>
    </button>
  </div>
</template>

<script>
export default {
  name: "LearningCycleCard",
  props: {
    autoPlay: {
      type: Boolean,
      default: true,
    },
    interval: {
      type: Number,
      default: 1800,
    },
  },
  data() {
    return {
      activeStepIndex: 0,
      cycleTimer: null,
      cycleSteps: [
        { keyword: 'Input',    desc: 'Read with AI assistance' },
        { keyword: 'Retain',   desc: 'Build vocabulary with spaced repetition flashcards' },
        { keyword: 'Activate', desc: 'Practice with AI chat tailored to your reading' },
        { keyword: 'Expand',   desc: 'Get reading recommendations based on your chats' },
        { keyword: 'Evolve',   desc: 'Repeat and level up' },
      ],
    };
  },
  mounted() {
    if (this.autoPlay) {
      this.startCycleAnimation();
    }
  },
  beforeUnmount() {
    if (this.cycleTimer) {
      clearInterval(this.cycleTimer);
    }
  },
  methods: {
    startCycleAnimation() {
      this.cycleTimer = setInterval(() => {
        this.activeStepIndex = (this.activeStepIndex + 1) % this.cycleSteps.length;
      }, this.interval);
    },
  },
};
</script>

<style scoped>
/* =============================================
   Learning Cycle Diagram
   ============================================= */
.cycle-diagram {
  --diagram-size: min(420px, calc(100vw - 150px));
  --node-dist: calc(var(--diagram-size) * 0.38);
  --ring-size: calc(var(--diagram-size) * 0.64);
  position: relative;
  width: var(--diagram-size);
  height: var(--diagram-size);
  margin: 0 auto;
  flex-shrink: 0;
}

.cycle-diagram::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(var(--ring-size) * 0.88);
  height: calc(var(--ring-size) * 0.88);
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(69, 74, 170, 0.05) 0%, transparent 70%);
  pointer-events: none;
}

/* Slowly-rotating dashed ring with orbiting dot */
.cycle-ring-track {
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--ring-size);
  height: var(--ring-size);
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px dashed rgba(69, 74, 170, 0.22);
  animation: cycle-ring-spin 9s linear infinite;
  animation-delay: -6.75s;
  pointer-events: none;
}

@keyframes cycle-ring-spin {
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

.cycle-ring-dot {
  position: absolute;
  top: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--gradient-aurora);
  box-shadow: 0 0 10px rgba(69, 74, 170, 0.55);
}

/* Center panel — shows active step info */
.cycle-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(var(--ring-size) * 0.56);
  text-align: center;
  pointer-events: none;
}

.cycle-center-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.cycle-center-keyword {
  font-size: clamp(14px, calc(var(--diagram-size) * 0.042), 40px);
  font-weight: 800;
  color: var(--color-primary-mid);
  font-family: var(--font-family);
}

.cycle-center-desc {
  font-size: clamp(10px, calc(var(--diagram-size) * 0.03), 13px);
  color: var(--color-text-secondary);
  line-height: 1.35;
}

/* Fade transition for center content */
.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.step-fade-enter-from {
  opacity: 0;
  transform: translateY(5px) scale(0.94);
}

.step-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.94);
}

/* Nodes — positioned via double-rotate trick */
.cycle-node {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(120px, calc(var(--diagram-size) * 0.24));
  transform:
    rotate(var(--sa))
    translateY(calc(-1 * var(--node-dist)))
    rotate(var(--ca))
    translate(-50%, -50%);
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 2;
}

.cycle-node-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  background: var(--color-surface);
  border: 1.5px solid rgba(69, 74, 170, 0.15);
  border-radius: 16px;
  padding: 16px 10px;
  min-height: 52px;
  transition: transform 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease, background 0.35s ease;
  box-shadow: var(--shadow-sm);
}

.cycle-node:hover .cycle-node-card {
  border-color: var(--color-aurora-start);
  background: rgba(69, 74, 170, 0.05);
  box-shadow: 0 0 0 3px rgba(69, 74, 170, 0.1), var(--shadow-md);
  transform: scale(1.08);
}

.cycle-node--active .cycle-node-card {
  border: 2px solid transparent;
  background: linear-gradient(var(--color-surface), var(--color-surface)) padding-box,
              var(--gradient-aurora) border-box;
  box-shadow: 0 0 0 4px rgba(69, 74, 170, 0.12), 0 0 16px rgba(229, 171, 114, 0.2), var(--shadow-md);
  transform: scale(1.1);
  animation: node-pulse 1.8s ease-in-out infinite;
}

@keyframes node-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(69, 74, 170, 0.12), var(--shadow-md); }
  50%       { box-shadow: 0 0 0 7px rgba(69, 74, 170, 0.18), 0 0 18px rgba(229, 171, 114, 0.25), var(--shadow-md); }
}

.cycle-node-label {
  font-size: clamp(11px, calc(var(--diagram-size) * 0.032), 15px);
  font-weight: 800;
  color: var(--color-primary);
  font-family: var(--font-family);
  white-space: nowrap;
}

.cycle-node--active .cycle-node-label {
  font-size: clamp(13px, calc(var(--diagram-size) * 0.038), 18px);
  background: var(--gradient-aurora);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
</style>
